/**
 * AudioGainBus — a WebAudio volume path for engines that refuse `.volume`.
 *
 * iOS Safari (and every browser wrapping WebKit on iOS, including Brave)
 * implements `HTMLMediaElement.volume` as READ-ONLY: assignments are accepted
 * silently and ignored, so the whole audio panel is decorative there — the
 * sliders move and nothing gets quieter. The only way to attenuate audio on
 * that platform is WebAudio: route the element through a GainNode, or play the
 * sound as a decoded buffer, and set the gain.
 *
 * ENGAGEMENT IS NOT A PROBE ANY MORE, AND THAT IS THE POINT. It used to be:
 * `writesVolume()` wrote 0.37 to a detached, source-less `<audio>` and read it
 * back, and only a swallowed write engaged the bus. That made one boolean the
 * single point of failure between the slider and the loudness — and on the
 * reporter's iPhone it was wrong. `691fc79e` is the proof: MUTE had to be
 * re-implemented with `.muted` because setting this bus's gain to 0 did not
 * silence the music, which it could only fail to do if the bus was not
 * carrying the element at all. What the player gets then is a bar that is an
 * on/off switch — every level above zero at full loudness, only zero audible
 * as a change. A read-back cannot rescue a getter that reflects a value the
 * platform then ignores, so nothing here asks the platform a question it can
 * answer wrongly: WHERE WEBAUDIO EXISTS, THE GAIN IS THE AUTHORITY, and the
 * callers pin `.volume` to 1 so the two can never multiply. Where WebAudio
 * does not exist, `.volume` stays the authority and this module is inert.
 *
 * `requiresGainBus()` survives, but only as what it always honestly was: an
 * answer to "may a plain element be trusted with a reduced level", used to
 * choose a FALLBACK. It no longer decides whether volume works at all.
 *
 * Two shapes, because the two channels play differently:
 *   - attachElement(el)  the one long-lived <audio> the soundtrack streams into
 *   - playBuffer(url, g) short SFX, decoded once and cached; the element
 *                        fallback uses a small warmed voice pool, because a
 *                        MediaElementSource per transient voice would leak
 *                        graph nodes
 *
 * Cross-origin: media is served from R2 behind `assets.moonfall.io` /
 * `assets-dev.moonfall.io` (it used to be Convex storage, which stamped every
 * blob `Cache-Control: private`). Those hosts carry
 * `access-control-allow-origin: *` from a zone Transform Rule, present with OR
 * without an `Origin` on the request — which is what closes the cache-poisoning
 * trap described in docs/asset-loading.md, where one header-less `<img>` fetch
 * could poison a key for a year. Anonymous CORS is therefore enough to keep the
 * graph untainted — but the element must carry `crossOrigin` BEFORE its `src`
 * is set, so attachElement() must be called on a fresh element.
 *
 * Contract: tests/unit/ui/audioGainBus.test.js.
 */
let AudioGainBus = {};

(function () {
  let elementGains = new WeakMap();     // media element -> GainNode
  const elementGraphs = new Set();      // iterable teardown ownership
  const pendingElements = new Set();    // awaiting a context that can run
  const commandedGains = new WeakMap(); // element -> last requested level
  const carryListeners = new Set();     // told when an element gains a gain
  let carriageInterrupted = false;      // the context left `running` while carrying
  let contextGeneration = 0;            // how many contexts this page has built
  const activeBufferGraphs = new Set(); // one-shot nodes awaiting `ended`
  const bufferLoadControllers = new Set();
  const decoded = new Map();            // url -> Promise<AudioBuffer>
  const readyBuffers = new Map();        // url -> decoded AudioBuffer
  const failedBufferLoads = new Map();   // url -> last failed wall-clock timestamp
  const BUFFER_FAILURE_COOLDOWN_MS = 5000;
  let context = null;
  let gainRequired = null;              // capability only; independent of context health
  let supported = null;                 // memoised probe result
  let failed = false;                   // a real failure; never retry silently
  let destroyed = false;
  /* THE PAGE HAS BEEN ACTIVATED AT LEAST ONCE. Remembered rather than inferred,
     because the activation that matters usually lands BEFORE this module owns
     anything to resume, and by the time a context exists the platform will only
     say whether it refuses, never why. See onGesture(). */
  let sawUserGesture = false;
  let resuming = false;                 // guards ensureContext() <-> resume()

  function contextClass() {
    return window.AudioContext || window.webkitAudioContext || null;
  }

  /**
   * ONE PAIR OF PAGE-WAKE LISTENERS FOR THE LIFE OF THE MODULE.
   *
   * These used to be registered inside ensureContext(), which is called again
   * for every context — and a repair REPLACES the context rather than reusing
   * it, so each interruption added another visibilitychange and another
   * pageshow handler that nothing ever removed. A long session with several
   * interruptions ended up running recover() a dozen times per wake.
   *
   * They read `context` at call time rather than closing over one, so a single
   * pair serves every context this module ever builds.
   */
  let pageWakeArmed = false;

  function armPageWakeRecovery() {
    if (pageWakeArmed) return;
    pageWakeArmed = true;
    document?.addEventListener?.('visibilitychange', () => {
      if (document.visibilityState === 'visible') recoverContext();
    });
    /* iOS can fire pagehide/pageshow with NO visibilitychange (soundtrack.js
       documents the sequence). Without this, that wake had no trigger at all
       except the player's next gesture. */
    window?.addEventListener?.('pageshow', () => recoverContext());
  }

  /* THE SCREEN GOING OFF IS THE CASE. Locking a phone hands the audio session
     away and the context goes to `suspended` — or, on WebKit, `interrupted`,
     the fourth state a `=== 'suspended'` guard misses entirely. Everything here
     can recover it, but only if something CALLS resume, and nothing was
     watching the context itself. Gated on the document being visible, so a page
     that is genuinely in the background stays silent. When the platform refuses
     to resume without a gesture (WebKit and Chrome on Android both do), the
     one-shot gesture resume is armed, so the first tap after returning restores
     sound. */
  function recoverContext() {
    const ctx = context;
    if (!ctx || ctx.state === 'running' || ctx.state === 'closed') return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    let settled;
    try {
      settled = ctx.resume?.();
    } catch (_) {
      armGestureResume();
      return;
    }
    Promise.resolve(settled).then(() => {
      /* Resolved is not the same as running — WebKit fulfils resume() on an
         'interrupted' context WITHOUT leaving 'interrupted'. This is the wake
         path after a screen lock took the iOS session (locking mid-SFX is the
         reliable repro), and only attaching .catch here meant nothing armed the
         gesture fallback: the game stayed silent while every volume control
         reported the right value. */
      if (ctx.state !== 'running' && ctx.state !== 'closed') armGestureResume();
    }).catch(() => armGestureResume());
  }

  /**
   * Does this engine honour a write to `.volume`? Restores the original value.
   * A missing element (or a throw) counts as "no", which keeps the bus off.
   */
  function writesVolume() {
    try {
      const probe = document.createElement('audio');
      const target = 0.37;
      probe.volume = target;
      return Math.abs(probe.volume - target) < 0.01;
    } catch (_) {
      return false;
    }
  }

  /**
   * WebAudio exists and has not failed. Deliberately NOT `requiresGainBus()`:
   * see the header — engaging only where a probe claims `.volume` is ignored
   * is what left the volume bar working as an on/off switch on a real phone.
   */
  function isSupported() {
    if (supported === null) supported = !failed && !!contextClass();
    return supported;
  }

  /** Only ever consulted to pick a FALLBACK, never to decide engagement. */
  function requiresGainBus() {
    if (gainRequired === null) gainRequired = !writesVolume();
    return gainRequired;
  }

  function ensureContext() {
    if (destroyed || context || !isSupported()) return context;
    try {
      const Ctor = contextClass();
      context = new Ctor();
      contextGeneration += 1;

      context.addEventListener?.('statechange', () => {
        noteCarriageInterruption();
        recoverContext();
        carryPendingElements();
        announceCarriageRepair();
      });
      armPageWakeRecovery();
      /* A CONTEXT IS BORN SUSPENDED, AND MUST NEVER BE BORN UNWATCHED.
         Every platform starts one suspended, and the two callers that build one
         without asking it to run — attachElement() for the music element and
         loadBuffer() for a decoded effect — left it that way with nothing armed:
         armGestureResume() was reachable only from a resume that had already
         been attempted and refused. On a lobby entry the player's one click
         arrives BEFORE either caller, so the tap was discarded (see onGesture),
         the context was then built silent, and no second gesture was coming.
         Music is inaudible through a stopped graph and every desktop sound
         effect is carried by the decoded-buffer path, so that one omission
         silenced BOTH channels for the life of the page. */
      if (context.state !== 'running') {
        if (sawUserGesture) resume();
        else armGestureResume();
      }
    } catch (error) {
      failed = true;
      supported = false;
      console.warn('[AudioGainBus] WebAudio unavailable; volume control is inert here:', error);
    }
    return context;
  }

  /**
   * A context that is not RUNNING is silent, so this must run on the same user
   * gesture that starts playback. Safe to call any number of times.
   *
   * The test is "not running", not "=== suspended". WebKit uses a fourth,
   * non-standard state — `'interrupted'` — whenever the iOS audio session is
   * taken away: backgrounding the app, an incoming call, another app claiming
   * audio. Guarding on `'suspended'` skipped exactly that state, so after a
   * minimise the soundtrack element happily resumed and played into a dead
   * graph while every bus-routed effect went nowhere. The page looked healthy
   * and produced no sound.
   */
  /* `click` and `touchend` are here because they are the activations the
     engines actually hand audio back on: WebKit releases an interrupted iOS
     session on `touchend`, and a `click` synthesised by the keyboard or by
     assistive technology may arrive with no pointer event before it at all.
     This list is now also what the module's own always-on listeners use, so
     "what counts as a gesture" is answered in exactly one place. */
  const GESTURE_EVENTS = ['pointerdown', 'mousedown', 'touchend', 'click', 'keydown'];
  let gestureResumeArmed = false;

  /**
   * Last resort: resume on the next real user gesture.
   *
   * Returning from a minimise is not a user gesture, and both WebKit and
   * Chrome on Android refuse to restart an interrupted/suspended context
   * without one — the promise simply rejects. Detecting `'interrupted'` was
   * necessary but not sufficient: the call it enabled was still made from a
   * visibilitychange handler, so the graph stayed dead and the page went on
   * looking healthy while producing no sound. Arm once, fire once, and stay
   * out of the way if the context recovers on its own.
   */
  function armGestureResume() {
    if (gestureResumeArmed) return;
    gestureResumeArmed = true;
    const fire = () => {
      GESTURE_EVENTS.forEach((type) => document.removeEventListener(type, fire, true));
      gestureResumeArmed = false;
      const ctx = context;
      if (!ctx || ctx.state === 'running' || ctx.state === 'closed') return;
      /* RE-ARM ON EVERY FAILURE. This used to be a bare
         `ctx.resume().catch(...)` whose comment claimed "the next gesture will
         try again" — but the listeners were already removed and the armed flag
         already cleared, so nothing was left watching. One post-unlock tap that
         failed to resume left the graph permanently dead until a
         visibilitychange/pageshow that had, by then, already fired. That is the
         reported "after unlock, SFX stay broken".

         Both refusals must re-arm, including the one that looks like success:
         WebKit fulfils resume() on an 'interrupted' context WITHOUT leaving
         'interrupted' — the same "resolved ≠ running" lesson resume() and
         recover() already carry. */
      let settled;
      try {
        settled = ctx.resume();
      } catch (_) {
        armGestureResume();
        return;
      }
      Promise.resolve(settled).then(() => {
        if (ctx.state !== 'running' && ctx.state !== 'closed') {
          armGestureResume();
          return;
        }
        /* AND TELL THE CHANNELS. `resume()` does exactly this on success and
           this path did not, so whenever the gesture fallback was the thing
           that brought the context back — which is the normal case on iOS,
           where an interrupted session is only handed over on a real user
           activation — the context returned to `running` and nobody was told.
           Pending elements stayed unrouted and the owning channel never learned
           its carriage had been interrupted, so the music was never rebuilt. */
        carryPendingElements();
        announceCarriageRepair();
      }).catch(() => armGestureResume());
    };
    GESTURE_EVENTS.forEach((type) => document.addEventListener(type, fire, true));
  }

  function resume() {
    if (resuming) return; // ensureContext() may call back in while we build one
    const ctx = context || ensureContext();
    if (!ctx || ctx.state === 'running' || ctx.state === 'closed') return;
    resuming = true;
    try {
      resumeNow(ctx);
    } finally {
      resuming = false;
    }
  }

  function resumeNow(ctx) {
    /* THIS IS AN OBSERVATION, AND IT NEEDS NO EVENT. Everything else that
       notices an interruption is reached through a page lifecycle event or a
       `statechange`, and nothing guarantees a phone fires either one across a
       lock. But a tap always lands here, and a context that is not running
       while it carries the music has been interrupted whether or not anybody
       said so. Without this, a device that stays silent through the whole lock
       leaves the repair unreachable — which looks exactly like not having
       written it, and is how two rounds of this shipped and changed nothing. */
    noteCarriageInterruption();
    let settled;
    try {
      settled = ctx.resume();
    } catch (error) {
      console.warn('[AudioGainBus] Could not resume the audio context:', error);
      armGestureResume();
      return;
    }
    Promise.resolve(settled).then(() => {
      // Resolved is not the same as running: a context refused without a
      // gesture can resolve and stay interrupted.
      if (ctx.state !== 'running' && ctx.state !== 'closed') {
        armGestureResume();
        return;
      }
      // It IS running now, so anything that was waiting on that can proceed —
      // again without needing a `statechange` to have been delivered.
      carryPendingElements();
      announceCarriageRepair();
    }).catch((error) => {
      console.warn('[AudioGainBus] Could not resume the audio context:', error);
      armGestureResume();
    });
  }

  /**
   * Route a media element through a GainNode. Returns true when the element's
   * loudness is now controlled by setElementGain(); false means the caller's
   * own `el.volume` path stays authoritative FOR NOW — the element is
   * remembered and routed the moment the context can run, and the caller is
   * told through onCarryChanged() so it can hand the level over.
   *
   * ROUTING A SUSPENDED CONTEXT WOULD BE A SILENT GAP, not a volume fix: an
   * element whose only output path is a graph that cannot run is inaudible,
   * and every platform starts its context suspended until a gesture. So
   * `crossOrigin` is claimed immediately — it must precede `src` or the later
   * graph is tainted, which plays as silence rather than as an error — and the
   * source node waits for a context that can be heard.
   *
   * @param {HTMLMediaElement} el a FRESH element whose `src` is not yet set
   */
  function attachElement(el) {
    if (!el || !isSupported()) return false;
    if (elementGains.has(el)) return true;
    try {
      el.crossOrigin = 'anonymous';
    } catch (_) { /* a locked-down element cannot be carried */ }
    const ctx = ensureContext();
    if (!ctx) return false;
    if (ctx.state !== 'running') {
      pendingElements.add(el);
      return false;
    }
    try {
      const source = ctx.createMediaElementSource(el);
      const gain = ctx.createGain();
      source.connect(gain);
      gain.connect(ctx.destination);
      elementGains.set(el, gain);
      elementGraphs.add({ source, gain });
      pendingElements.delete(el);
      const commanded = commandedGains.get(el);
      if (typeof commanded === 'number') applyGain(gain, commanded);
      return true;
    } catch (error) {
      pendingElements.delete(el);
      console.warn('[AudioGainBus] Could not route the element; volume stays inert:', error);
      return false;
    }
  }

  /** Route everything that was waiting for a context it could be heard through. */
  function carryPendingElements() {
    if (!pendingElements.size) return;
    if (!context || context.state !== 'running') return;
    let carried = false;
    for (const el of [...pendingElements]) {
      if (attachElement(el)) carried = true;
    }
    if (!carried) return;
    notifyCarryListeners();
  }

  function notifyCarryListeners() {
    for (const listener of [...carryListeners]) {
      try { listener(); } catch (_) { /* a channel's own problem */ }
    }
  }

  /**
   * An interruption does not have to come with a page lifecycle event: a phone
   * call, Siri, or another app claiming audio takes the session while the
   * player is still looking at the screen. The channel's wake path never runs
   * for those, so the context's own recovery is the only signal there is.
   */
  function announceCarriageRepair() {
    if (!carriageInterrupted) return;
    if (!context || context.state !== 'running') return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    notifyCarryListeners();
  }

  /**
   * A CARRIED ELEMENT MAY NOT SURVIVE AN INTERRUPTION, and no page can measure
   * that. WebKit does not guarantee that a MediaElementAudioSourceNode still
   * produces output after the iOS audio session is taken and handed back: the
   * context can return to `running` while that one node stays silent for the
   * rest of the session, with `paused === false`, `currentTime` advancing and
   * every control reporting the right value. The only honest response is to
   * rebuild the element and its graph, so this records that the precondition
   * happened and the owning channel decides.
   *
   * Deliberately narrow: an ordinary tab switch does not suspend an audible
   * context, so this stays false and nothing is rebuilt for it.
   */
  function noteCarriageInterruption({ force = false } = {}) {
    if (!elementGraphs.size) return;
    if (force || (context && context.state !== 'running' && context.state !== 'closed')) {
      carriageInterrupted = true;
    }
  }

  /**
   * THROW THE CONTEXT AWAY, NOT JUST THE NODE.
   *
   * A new `MediaElementAudioSourceNode` on the context that was interrupted is
   * still a node on the context that was interrupted, and WebKit makes no
   * promise about either. Reported from the device after the volume half of
   * this work shipped — "music vol is fixed, but music restore after close
   * screen was still broken" — and the volume fix is precisely what made this
   * reachable: before it the music element was never routed through WebAudio on
   * iOS at all, so the graph could not be what died.
   *
   * Decoded buffers are deliberately KEPT. An `AudioBuffer` is not bound to the
   * context that decoded it, and dropping the cache would silence every sound
   * effect until it re-downloaded — a second, louder regression in the middle of
   * recovering from the first. `playBuffer` already guards its own `try`, so an
   * engine that disagrees falls back rather than throwing.
   */
  function renewContext() {
    for (const controller of bufferLoadControllers) {
      try { controller.abort(); } catch (_) { }
    }
    bufferLoadControllers.clear();
    stopBuffers();
    for (const { source, gain } of elementGraphs) {
      try { source.disconnect(); } catch (_) { }
      try { gain.disconnect(); } catch (_) { }
    }
    elementGraphs.clear();
    elementGains = new WeakMap();
    pendingElements.clear();
    // In-flight decodes belong to the context being discarded; `loadBuffer`
    // already refuses to store a result whose context is no longer current.
    decoded.clear();
    carriageInterrupted = false;
    const discarded = context;
    context = null;
    if (discarded && discarded.state !== 'closed' && typeof discarded.close === 'function') {
      Promise.resolve(discarded.close()).catch(() => {});
    }
  }

  /**
   * A read-only description of the audio context, for diagnostics.
   *
   * `generation` is what separates "running again" from "a DIFFERENT one is
   * running", which is the entire question when a repair replaces the context
   * rather than reusing it. Deliberately a real export and not the
   * `_contextForTests` seam: that seam reaches the page only through the test
   * bridge, so anything reading it off `window.UI` is dead code in the shipped
   * bundle — exactly where a diagnostic is needed.
   */
  function describeContext() {
    let currentTime = null;
    try {
      /* THE GRAPH'S OWN CLOCK. It advances only while the context renders, so
         a channel that reads it before the page is hidden and again on the way
         back can tell whether the graph ran the whole time — without being
         told. That is the one observation a screen lock cannot hide: the
         `statechange` pair for an interruption that began and ended while the
         page was frozen is delivered afterwards with both events reading
         `running`, so the flag below is never set for it. */
      if (context && typeof context.currentTime === 'number') currentTime = context.currentTime;
    } catch (_) { /* a context that refuses to be read is a context with no clock */ }
    return { generation: contextGeneration, state: context ? context.state : null, currentTime };
  }

  /**
   * A context that exists but cannot be heard through: not running, and not
   * closed either (a closed one is never carrying anything — renewContext()
   * and clearRuntime() drop every graph before they close it). WebKit's
   * `interrupted` lands here, which a `=== 'suspended'` test misses.
   */
  function contextStalled() {
    return Boolean(context)
      && context.state !== 'running'
      && context.state !== 'closed';
  }

  /**
   * Is `el` routed through this bus's graph right now? A pending element — one
   * the bus declined because the context could not run — is heard directly and
   * is NOT carried.
   */
  function carriesElement(el) {
    return Boolean(el) && elementGains.has(el);
  }

  /**
   * A CARRIED ELEMENT IS ONLY PLAYING IF THE GRAPH CARRYING IT CAN RUN.
   *
   * Once an element is routed through a MediaElementAudioSourceNode its audio
   * goes into the graph and only into the graph. When the audio session is
   * taken and the context stops, the graph outputs silence — and the element
   * does not care: measured in WebKit and Firefox with a routed element and a
   * stopped context, `paused` stays false and `currentTime` keeps advancing,
   * and a second `play()` (which is what the iOS notification bar does)
   * resolves and changes nothing. So the element's own state is not an oracle
   * for whether it can be heard; this is. Read-only, needs no event, and is
   * true exactly when consumeCarriageInterruption() has a rebuild to offer.
   */
  function carriageStalled(el) {
    return carriesElement(el) && contextStalled();
  }

  /**
   * True once per interruption, for the channel that owns `el`.
   *
   * Read from the STATE as well as the flag. The flag is set by a `statechange`
   * listener, and nothing guarantees WebKit fires one when the iOS audio
   * session is taken — the spec requires the state to change, not that anyone
   * is told. A context that is sitting in `interrupted`/`suspended` while it
   * carries the music has been interrupted whether or not it announced itself,
   * and missing that is indistinguishable from having no repair at all.
   */
  function consumeCarriageInterruption(el) {
    if (!carriesElement(el)) return false;
    if (!carriageInterrupted && !contextStalled()) return false;
    carriageInterrupted = false;
    return true;
  }

  /** Called when an element's loudness changes hands, so channels re-apply. */
  function onCarryChanged(listener) {
    if (typeof listener === 'function') carryListeners.add(listener);
  }

  function applyGain(gain, level) {
    try {
      gain.gain.setValueAtTime(level, (context && context.currentTime) || 0);
    } catch (_) {
      gain.gain.value = level;
    }
  }

  /** @returns {boolean} true when the gain was applied by this bus. */
  function setElementGain(el, value) {
    if (!el) return false;
    const level = Math.max(0, Math.min(1, Number(value) || 0));
    commandedGains.set(el, level);
    let gain = elementGains.get(el);
    if (!gain && pendingElements.has(el)) {
      // A volume change is as good a moment as a statechange to notice that
      // the context has come up.
      attachElement(el);
      gain = elementGains.get(el);
    }
    if (!gain) return false;
    applyGain(gain, level);
    return true;
  }

  function loadBuffer(url) {
    if (readyBuffers.has(url)) return Promise.resolve(readyBuffers.get(url));
    if (decoded.has(url)) return decoded.get(url);
    const failedAt = failedBufferLoads.get(url);
    if (
      Number.isFinite(failedAt)
      && Date.now() - failedAt < BUFFER_FAILURE_COOLDOWN_MS
    ) {
      return Promise.resolve(null);
    }
    failedBufferLoads.delete(url);
    const ctx = ensureContext();
    if (!ctx) return Promise.resolve(null);
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    if (controller) bufferLoadControllers.add(controller);
    const pending = fetch(url, {
      mode: 'cors',
      ...(controller ? { signal: controller.signal } : {}),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.arrayBuffer();
      })
      .then((bytes) => ctx.decodeAudioData(bytes))
      .then((buffer) => {
        if (buffer && !destroyed && context === ctx) {
          failedBufferLoads.delete(url);
          readyBuffers.set(url, buffer);
          return buffer;
        }
        return null;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError' && !destroyed) {
          failedBufferLoads.set(url, Date.now());
          console.warn('[AudioGainBus] Could not decode a sound effect:', error);
        }
        return null;
      })
      .finally(() => {
        decoded.delete(url);
        if (controller) bufferLoadControllers.delete(controller);
      });
    decoded.set(url, pending);
    return pending;
  }

  /**
   * ONE DOWNLOAD PER SOUND. SoundEffects keeps two `<audio>` voices per cue as
   * the fallback for a context that is not running yet (the very first tap),
   * and each used to request the file itself — three requests for every cue,
   * measured 25 September 2026 on every boot and every match start. The voices
   * now wait for the one download the buffer makes and then load the same URL
   * in the same CORS mode, which the HTTP cache answers (the asset host's
   * `Vary: Origin` keeps CORS and no-CORS copies apart).
   *
   * Never a blob: URL: the page's Content-Security-Policy `media-src` allows
   * only 'self' and the asset host, so every engine refused voices streamed
   * from one and the first tap's fallback voice was silent.
   *
   * Resolves to the URL to load, once the download has landed (or failed:
   * then the voices fetch it themselves, as before).
   */
  function sourceUrl(url) {
    if (!url) return Promise.resolve(null);
    if (!isSupported()) return Promise.resolve(url);
    return loadBuffer(url).then(() => url, () => url);
  }

  /** Warm the cache so the first play is not late. Best-effort. */
  function preloadBuffer(url) {
    if (!url || !isSupported()) return Promise.resolve(null);
    return loadBuffer(url);
  }

  /**
   * Play a one-shot at `value` gain. Returns true when this bus took the play,
   * false when the caller should use its own element path.
   *
   * The return value is a PROMISE TO BE HEARD, not a promise to try. A
   * suspended context accepts `source.start(0)` and silently drops it, so
   * claiming the play while suspended made every effect inaudible while the
   * code looked healthy — and skipped the element fallback that would have
   * been heard. Two ways in: iOS starts contexts suspended until a user
   * gesture, and `release()` suspends deliberately when the player leaves a
   * game. So the bus only takes a sound once the context is actually running,
   * and still kicks a resume on the way past so the next one can be its own.
   */
  function playBuffer(url, value) {
    if (!url || !isSupported()) return false;
    const ctx = ensureContext();
    if (!ctx) return false;
    if (ctx.state !== 'running') {
      void loadBuffer(url);
      resume();
      return false;
    }
    const buffer = readyBuffers.get(url);
    if (!buffer) {
      void loadBuffer(url);
      return false;
    }
    let graph = null;
    try {
      const source = ctx.createBufferSource();
      const gain = ctx.createGain();
      gain.gain.value = Math.max(0, Math.min(1, Number(value) || 0));
      source.buffer = buffer;
      source.connect(gain);
      gain.connect(ctx.destination);
      graph = { source, gain };
      activeBufferGraphs.add(graph);
      source.addEventListener('ended', () => {
        activeBufferGraphs.delete(graph);
        try { source.disconnect(); gain.disconnect(); } catch (_) { /* already gone */ }
      });
      source.start(0);
      return true;
    } catch (error) {
      if (graph) {
        activeBufferGraphs.delete(graph);
        try { graph.source.disconnect(); } catch (_) { }
        try { graph.gain.disconnect(); } catch (_) { }
      }
      console.warn('[AudioGainBus] Could not start a sound effect:', error);
      return false;
    }
  }

  /**
   * Let go of the audio hardware.
   *
   * A RUNNING AudioContext holds the platform audio session open on its own,
   * independently of any element — so on iOS the Control Center now-playing
   * control survives a game the player has left even after every element is
   * paused and released. Suspending is enough and is reversible: `resume()`
   * already exists and is called on the next user gesture.
   */
  function release() {
    if (!context) return false;
    try {
      if (typeof context.suspend === 'function' && context.state === 'running') {
        context.suspend();
        noteCarriageInterruption({ force: true });
        return true;
      }
    } catch (_) {
      // A closed or unusable context needs no release.
    }
    return false;
  }

  function onGesture() {
    /* RECORD THE ACTIVATION WHETHER OR NOT THERE IS ANYTHING TO RESUME YET.
       This was a bare `if (context) resume()`, which silently discarded the
       one gesture most pages get: in an ordinary lobby entry the player clicks
       once, and no channel has built a context at that instant. ensureContext()
       then made a suspended context a moment later with nothing armed, and the
       page went on looking healthy while producing no sound at all.
       A random tap still must not ALLOCATE audio hardware, so this remembers
       the activation and leaves the allocation to the channel that wants it. */
    sawUserGesture = true;
    if (context) resume();
  }

  function stopBuffers() {
    let stopped = 0;
    for (const { source, gain } of activeBufferGraphs) {
      try { source.stop?.(0); } catch (_) { }
      try { source.disconnect(); } catch (_) { }
      try { gain.disconnect(); } catch (_) { }
      stopped += 1;
    }
    activeBufferGraphs.clear();
    return stopped;
  }

  function clearRuntime() {
    for (const controller of bufferLoadControllers) {
      try { controller.abort(); } catch (_) { }
    }
    bufferLoadControllers.clear();
    stopBuffers();
    pendingElements.clear();
    carriageInterrupted = false;
    for (const { source, gain } of elementGraphs) {
      try { source.disconnect(); } catch (_) { }
      try { gain.disconnect(); } catch (_) { }
    }
    elementGraphs.clear();
    elementGains = new WeakMap();
    decoded.clear();
    readyBuffers.clear();
    failedBufferLoads.clear();
    const ownedContext = context;
    context = null;
    if (ownedContext && ownedContext.state !== 'closed' && typeof ownedContext.close === 'function') {
      Promise.resolve(ownedContext.close()).catch(() => {});
    }
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    window.removeEventListener('pagehide', release);
    GESTURE_EVENTS.forEach((type) => document.removeEventListener(type, onGesture, true));
    clearRuntime();
  }

  window.addEventListener('pagehide', release);
  /* Capture, and every gesture the fallback knows about. The module used to
     watch only `pointerdown` and `keydown` here while armGestureResume() below
     watched a different, wider set — so which taps could revive audio depended
     on which of the two happened to be listening. */
  GESTURE_EVENTS.forEach((type) => {
    document.addEventListener(type, onGesture, { capture: true, passive: true });
  });
  AudioGainBus = {
    isSupported,
    requiresGainBus,
    resume,
    release,
    attachElement,
    setElementGain,
    onCarryChanged,
    carriesElement,
    carriageStalled,
    consumeCarriageInterruption,
    renewContext,
    describeContext,
    preloadBuffer,
    sourceUrl,
    playBuffer,
    stopBuffers,
    destroy,
    // Test seam: the probe memoises, and specs need to re-run it per scenario.
    _reset() {
      clearRuntime();
      carryListeners.clear();
      carriageInterrupted = false;
      destroyed = false;
      gainRequired = null;
      supported = null;
      failed = false;
      sawUserGesture = false;
      resuming = false;
    },
    _contextForTests() { return context; },
  };
})();

export { AudioGainBus };
